'use client';
import React from 'react';
import styled, { TC } from '@benzinga/themetron';
import { PerformanceColor } from '../../Ticker/PerformanceColor';
import { StockSymbol } from '@benzinga/session';
import { getQuoteChange, Quote } from '@benzinga/quotes-manager';
import { QuoteInfo } from '../../Ticker/QuoteInfo';
import { useQuoteSubscription, useDetailedQuote } from '@benzinga/quotes-manager-hooks';
import { getTickerDisplayName } from '../../../utils';
import { TickerContext } from '../context';

interface Props {
  onSymbolClick: (symbol: StockSymbol) => void;
}

export const TickerHeaderModule: React.FC<Props> = props => {
  const ticker = React.useContext(TickerContext);

  const { details: quoteDetail, quote } = useQuoteSubscription(ticker.symbol);
  const detailedQuote = useDetailedQuote(ticker.symbol)?.ok;

  const displayName = React.useMemo(
    () => getTickerDisplayName(quoteDetail?.companyName, detailedQuote?.name, ticker.symbol),
    [quoteDetail?.companyName, detailedQuote?.name, ticker.symbol],
  );

  const onSymbolClick = React.useCallback(() => {
    ticker.onClose();
    const onSymbolClick = props.onSymbolClick;
    onSymbolClick(ticker.symbol);
  }, [props.onSymbolClick, ticker]);

  const { nominalChange, percentageChange } = getQuoteChange<Quote>(quote);

  return (
    <TickerTextContainerDiv onClick={onSymbolClick} role="button">
      <TitleHolder>
        <LargeTextDiv title={quoteDetail?.companyName ?? displayName}>
          {displayName} ({ticker.symbol})
        </LargeTextDiv>
      </TitleHolder>
      <TickerInfoDiv>{quoteDetail && <QuoteInfo symbol={ticker.symbol} />}</TickerInfoDiv>
      <TickerPriceContainerRow>
        <TickerPriceDiv>{quote?.currentPriceFormatted}</TickerPriceDiv>
        <PerformanceContainerRow>
          <PerformanceColorPaddingDiv>
            <PerformanceColor hasArrow stat={nominalChange} />
          </PerformanceColorPaddingDiv>{' '}
          <PerformanceColor isPercentage stat={percentageChange} />
        </PerformanceContainerRow>
      </TickerPriceContainerRow>
    </TickerTextContainerDiv>
  );
};

const TitleHolder = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LargeTextDiv = styled.div`
  font-size: 1.5em;
  font-weight: 500;
  margin-bottom: 4px;
  min-width: 0;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TickerPriceDiv = styled.div`
  display: inline-block;
  font-size: 1.5em;
  font-weight: 500;
  margin-right: 0.25em;
`;

const TickerInfoDiv = styled.div`
  font-weight: 300;
  font-size: 0.8em;
  white-space: nowrap;
`;

const TickerPriceContainerRow = styled(TC.Row)`
  align-items: baseline;
  padding-top: 5px;
`;

const PerformanceContainerRow = styled(TC.Row)`
  font-size: 1em;
  white-space: nowrap;
`;

const TickerTextContainerDiv = styled(TC.Div)`
  min-width: 0;
  max-width: 100%;
  padding: 10px 12px;
`;

const PerformanceColorPaddingDiv = styled.div`
  padding-right: 0.25em;
`;
